import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = '';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      sessionStorage.setItem('admin_session', 'true');
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-saffron/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Lock size={24} className="text-saffron" />
          </div>
          <h1 className="font-display text-2xl font-bold text-earth-dark tracking-tight">Admin Portal</h1>
          <p className="text-sm text-earth/50 font-sans mt-1">ASJK Laxmi Global</p>
        </div>

        {/* Google Sign-in */}
        <button
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center gap-3 bg-white border border-earth/15 rounded-xl py-3.5 px-4 font-sans font-semibold text-sm text-earth-dark hover:bg-gray-50 transition-colors shadow-sm mb-4"
          data-testid="google-signin-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 border-t border-earth/10" />
          <span className="text-[10px] uppercase tracking-wider text-earth/30 font-sans font-semibold">or</span>
          <div className="flex-1 border-t border-earth/10" />
        </div>

        <form onSubmit={handleLogin} className="bg-white rounded-xl shadow-card-lift border border-saffron-100/50 p-8 space-y-5" data-testid="login-form">
          {error && (
            <div className="bg-chilli/5 border border-chilli/20 text-chilli text-sm font-sans px-4 py-3 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Email Address</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-earth/30" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@asjklaxmiglobal.com"
                required
                className="w-full pl-10 pr-4 py-3 border border-earth/15 rounded-lg text-sm font-sans text-earth-dark bg-cream/30 focus:ring-2 focus:ring-saffron/30 focus:border-saffron focus:outline-none transition-colors"
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Password</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-earth/30" />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full pl-10 pr-12 py-3 border border-earth/15 rounded-lg text-sm font-sans text-earth-dark bg-cream/30 focus:ring-2 focus:ring-saffron/30 focus:border-saffron focus:outline-none transition-colors"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-earth/30 hover:text-earth/60 transition-colors"
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-saffron text-white font-sans font-semibold text-sm py-3.5 rounded-lg hover:bg-saffron-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-saffron-glow"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight size={16} />}
          </button>
        </form>

        <p className="text-center text-[10px] text-earth/30 font-sans mt-6">
          FSSAI: 10018022007595 &middot; IEC: CNUPK2717H &middot; GST Registered
        </p>
      </motion.div>
    </div>
  );
}
